import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  ordersApi,
  orderPromotion,
  orderServiceFee,
  orderAmountPaid,
  serviceGrossOf,
  AdminOrderDetailResponse,
  ProcessRefundRequest,
  OrderTimelineEvent,
  AssignDriverRequest,
  ResolveDisputeRequest,
  PickupDeliveryResponse,
  formatScheduled,
  selectedOptions,
} from '../services/ordersApi';
import OrderStatusBadge from '../components/orders/OrderStatusBadge';
import AssignDriverModal, { CurrentLegProvider } from '../components/orders/AssignDriverModal';
import ResolveDisputeModal from '../components/orders/ResolveDisputeModal';
import AdminCancelOrderModal from '../components/orders/AdminCancelOrderModal';
import RescheduleOrderModal from '../components/orders/RescheduleOrderModal';
import { useToast, ToastView } from '../components/promotions/useToast';
import PaymentBlock from '../components/payments/PaymentBlock';
import ChargebackBanner from '../components/payments/ChargebackBanner';
import { adminPaymentsApi, AdminPaymentDetail, AdminPaymentDispute } from '../services/paymentsApi';
import InvoicePanel from '../components/orders/InvoicePanel';
import { adminOrderInvoicesApi } from '../services/invoicesApi';
import OrderLegsPanel, { legMethodLabel, legTakesDriver, TransportBadge } from '../components/orders/OrderLegsPanel';
import RefundConsequence, { useRefundPreview } from '../components/orders/RefundConsequence';
import DeliveryFeeRefundCheckbox from '../components/orders/DeliveryFeeRefundCheckbox';
import ServiceFeeRefundCheckbox, { serviceFeeRefundChecked } from '../components/orders/ServiceFeeRefundCheckbox';
import InfoTip from '../components/finance/InfoTip';
import { ApiError } from '../services/api';
import { FAULT_PARTY_LABELS, FaultParty, FeeBorneBy, serviceCeilingOf } from '../types/refunds';
import ActorChip from '../components/audit/ActorChip';
import { settlementsApi, CreditNoteResponse } from '../services/settlementsApi';

const FAULT_PARTIES: FaultParty[] = ['GARAGE', 'PLATFORM', 'CUSTOMER', 'NONE'];

// #716 — COMPLETED too, with no time limit (Maher, 2026-09-28): only an admin can dispute a completed
// order; settlement holds the garage's payout until the dispute is resolved.
const DISPUTABLE_STATUSES = ['CREATED', 'PICKUP_ASSIGNED', 'IN_PROGRESS', 'SERVICE_COMPLETE', 'DROPOFF_ASSIGNED', 'COMPLETED'];

// Reassign is allowed while a leg is awaiting or has an assigned driver.
const REASSIGNABLE_STATUSES = ['PICKUP_AWAITING_DRIVER', 'PICKUP_ASSIGNED', 'DROPOFF_AWAITING_DRIVER', 'DROPOFF_ASSIGNED'];

type ActiveModal = 'assignPickup' | 'assignDropoff' | 'reassign' | 'disputes' | 'resolve' | 'refund' | 'reschedule' | null;

/** BR-26 — the provider frozen onto a leg, for the reassign dialog to pre-select; null when none. */
function legProvider(leg: PickupDeliveryResponse | undefined): CurrentLegProvider | null {
  return leg?.providerId
    ? {
      providerId: leg.providerId,
      providerName: leg.providerName ?? null,
      // Spec 072 — the frozen insurance snapshot, which a "keep current" reassignment keeps.
      showDisclaimer: leg.providerShowDisclaimer ?? null,
      insuredAmount: leg.providerInsuredAmount ?? null,
      disclaimerEn: leg.providerDisclaimerEn ?? null,
    }
    : null;
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="admin-field">
      <div className="admin-field-label">{label}</div>
      <div className="admin-field-value">{value ?? '—'}</div>
    </div>
  );
}

export default function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState<AdminOrderDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ActiveModal>(null);
  const { toast, show: showToast } = useToast();

  // Refund modal state
  const [refundType, setRefundType] = useState<'FULL' | 'PARTIAL'>('FULL');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundError, setRefundError] = useState<string | null>(null);
  // Spec 053 Flow B — who is responsible (required) + whether Garagat absorbs the gateway fee.
  const [faultParty, setFaultParty] = useState<FaultParty | null>(null);
  // #1059 — undefined = follow billing's default (Garagat absorbs the gateway fee on its own fault).
  const [platformBearsFee, setPlatformBearsFee] = useState<boolean | undefined>(undefined);
  // Spec 053 Flow F5 / BR-25 — also reverse Garagat's own pickup & delivery fee (issues a PCN).
  const [refundDeliveryFee, setRefundDeliveryFee] = useState(false);
  // Spec 068 AC-18 — undefined = follow billing's default for the fault party; set once the admin ticks/unticks.
  const [refundServiceFee, setRefundServiceFee] = useState<boolean | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);

  const feeBorneBy: FeeBorneBy | undefined =
    platformBearsFee === undefined ? undefined : platformBearsFee ? 'PLATFORM' : 'CUSTOMER';
  const parsedRefundAmount = refundType === 'PARTIAL' ? parseFloat(refundAmount) : NaN;
  // FULL → null so the preview defaults to the ceiling. PARTIAL with an empty/invalid amount must NOT
  // fall back to the ceiling (that would preview a full refund), so the preview is disabled instead.
  const previewEnabled = refundType === 'FULL' || parsedRefundAmount > 0;
  const previewAmount = refundType === 'PARTIAL' && previewEnabled ? parsedRefundAmount : null;

  // Spec 053 Phase 3: the amount field is the SERVICE slice, so it is measured against the service
  // gross and the preview's service ceiling. Garagat's delivery fee is never part of that basis —
  // it is a separate supply, refunded only by the explicit checkbox (BR-25).
  const serviceGross = order ? serviceGrossOf(order) : null;

  const { preview: refundPreview, loading: refundPreviewLoading, error: refundPreviewError, pending: refundPreviewPending } =
    useRefundPreview({
      paymentId: modal === 'refund' ? order?.paymentId ?? null : null,
      faultParty,
      amount: previewAmount,
      enabled: previewEnabled,
      feeBorneBy,
      refundDeliveryFee,
      refundServiceFee,
      token: auth.token ?? '',
    });

  // Spec 068 — the checkbox SHOWS the admin's choice, else the preview's policy default; only an
  // explicit choice is ever SENT (design §3.6: omitted → billing applies the default for the fault
  // party). The fault-party handler drops the explicit choice, since the default follows the party.
  const serviceFeeCharged = refundPreview?.serviceFeeCharged ?? null;
  const serviceFeeTicked = serviceFeeRefundChecked(refundServiceFee, refundPreview);
  const selectFaultParty = (party: FaultParty) => {
    setFaultParty(party);
    setRefundServiceFee(undefined);
    setPlatformBearsFee(undefined);
  };
  // #1059 — the processing-fee box SHOWS the admin's choice, else the bearer billing's preview resolved.
  const platformFeeTicked = platformBearsFee ?? refundPreview?.feeBorneBy === 'PLATFORM';

  const deliveryFeeCharged = order?.deliveryFeeGross ?? refundPreview?.deliveryFeeCharged ?? null;
  const serviceCeiling = serviceCeilingOf(refundPreview, serviceGross);

  const resetRefundForm = () => {
    setRefundReason('');
    setRefundAmount('');
    setRefundType('FULL');
    setFaultParty(null);
    setPlatformBearsFee(undefined);
    setRefundDeliveryFee(false);
    setRefundServiceFee(undefined);
    setRefundError(null);
  };

  const closeRefundModal = () => {
    setModal(null);
    resetRefundForm();
  };

  const fetchOrder = useCallback(() => {
    if (!orderId || !auth.token) return;
    setLoading(true);
    ordersApi.getOrderDetail(orderId, auth.token)
      .then(setOrder)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [orderId, auth.token]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  // Spec 051 AC-20 + Spec 052: payment + chargebacks from billing. Fetched together so the
  // banner and the block agree; either failing degrades independently (the page still renders).
  const [payment, setPayment] = useState<AdminPaymentDetail | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [disputes, setDisputes] = useState<AdminPaymentDispute[]>([]);
  const paymentId = order?.paymentId ?? null;

  const fetchPayment = useCallback(() => {
    if (!paymentId || !auth.token) return;
    setPaymentLoading(true);
    setPaymentFailed(false);
    Promise.all([
      adminPaymentsApi.getPayment(paymentId, auth.token),
      adminPaymentsApi.listDisputes(paymentId, auth.token).catch(() => [] as AdminPaymentDispute[]),
    ])
      .then(([p, d]) => { setPayment(p); setDisputes(d); })
      .catch(() => setPaymentFailed(true))
      .finally(() => setPaymentLoading(false));
  }, [paymentId, auth.token]);

  useEffect(() => { fetchPayment(); }, [fetchPayment]);

  // Spec 053 Flow A2 — a refund on an invoiced settlement issues a CCN; the order already knows its
  // settlement and refund ids, so one list call finds the note (no per-refund endpoint needed).
  const [creditNote, setCreditNote] = useState<CreditNoteResponse | null>(null);
  const settlementIdForCcn = order?.settlementId ?? null;
  const refundIdForCcn = order?.refundId ?? null;
  useEffect(() => {
    if (!auth.token || !settlementIdForCcn || !refundIdForCcn) {
      setCreditNote(null);
      return;
    }
    let cancelled = false;
    settlementsApi.listCreditNotes(settlementIdForCcn, auth.token)
      .then((notes) => {
        if (!cancelled) setCreditNote(notes.find((n) => n.refundId === refundIdForCcn) ?? null);
      })
      .catch((err) => console.error('Failed to load credit notes for order:', err));
    return () => { cancelled = true; };
  }, [auth.token, settlementIdForCcn, refundIdForCcn]);

  // #468 — the customer credit note's PDF, through the same presigned-URL endpoint the invoice
  // panel uses (the raw S3 key is never exposed). 409 while the document is still being prepared.
  // Its own error state, never `setError`: that one replaces the whole page, and a failed download
  // must not hide the order.
  const [creditNoteError, setCreditNoteError] = useState<string | null>(null);
  const openCreditNote = async (documentId: string) => {
    if (!order || !auth.token) return;
    setCreditNoteError(null);
    try {
      const { downloadUrl } = await adminOrderInvoicesApi.getDownloadUrl(order.id, documentId, auth.token);
      window.open(downloadUrl, '_blank');
    } catch (err) {
      setCreditNoteError(err instanceof ApiError && err.status === 409
        ? 'The credit note is still being prepared'
        : err instanceof Error ? err.message : 'Credit note download failed');
    }
  };

  // ── Action handlers ──
  const handleAssignPickup = async (req: AssignDriverRequest) => {
    if (!order || !auth.token) return;
    const updated = await ordersApi.assignPickupDriver(order.id, req, auth.token);
    setOrder(updated);
    setModal(null);
  };

  const handleAssignDropoff = async (req: AssignDriverRequest) => {
    if (!order || !auth.token) return;
    const updated = await ordersApi.assignDropoffDriver(order.id, req, auth.token);
    setOrder(updated);
    setModal(null);
  };

  const handleReassign = async (req: AssignDriverRequest) => {
    if (!order || !auth.token) return;
    const updated = order.status.startsWith('PICKUP_')
      ? await ordersApi.reassignPickupDriver(order.id, req, auth.token)
      : await ordersApi.reassignDropoffDriver(order.id, req, auth.token);
    setOrder(updated);
    setModal(null);
  };

  // Both of these were previously reachable only from PICKUP_ASSIGNED / DROPOFF_ASSIGNED, where a
  // driver had already done the work and the admin was only recording it. Spec 056 also offers them
  // on a SELF leg (CREATED and SERVICE_COMPLETE), so they now carry a confirmation and — more
  // importantly — surface their own failures: without the catch a rejected call was an unhandled
  // rejection and the admin saw a button that silently did nothing.
  const handleMarkInProgress = async () => {
    if (!order || !auth.token) return;
    if (!window.confirm('Confirm the vehicle is at the garage? This starts the service.')) return;
    try {
      setOrder(await ordersApi.markInProgress(order.id, auth.token));
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleMarkComplete = async () => {
    if (!order || !auth.token) return;
    // This stamps completedAt and publishes OrderCompletedEvent — it starts settlement and cannot
    // be undone, so it asks first wherever it is offered from.
    if (!window.confirm('Confirm the customer has the vehicle? This completes the order and starts settlement.')) return;
    try {
      setOrder(await ordersApi.markComplete(order.id, auth.token));
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleResolve = async (req: ResolveDisputeRequest) => {
    if (!order || !auth.token) return;
    // On 400 (Spec 053 BR-4: faultParty missing for a refund) / 422 / 502 this throws — the modal
    // surfaces the error message and stays open, and the order remains DISPUTED.
    const updated = await ordersApi.resolveDispute(order.id, req, auth.token);
    setOrder(updated);
    setModal(null);
  };

  // Spec 077 — the server answers with the fresh order; the page swaps it in and says so.
  const handleRescheduled = (updated: AdminOrderDetailResponse) => {
    setOrder(updated);
    setModal(null);
    showToast('Order rescheduled. Customer and garage have been notified.');
  };

  const handleRescheduleConflict = () => {
    setModal(null);
    showToast('Someone else just changed this order — reloaded', 'error');
    fetchOrder();
  };

  const handleDisputesSuccess = () => {
    setModal(null);
    fetchOrder();
  };

  const handleRefundSubmit = async () => {
    if (!orderId || !order || !auth.token) return;
    setRefundError(null);
    if (!faultParty) { setRefundError('Select who is responsible for this refund'); return; }
    // Never submit against the previous inputs' preview (Spec 068 review: stale fault party).
    if (refundPreviewPending) { setRefundError('Wait for the refund preview to finish'); return; }
    if (refundReason.length < 10) { setRefundError('Reason must be at least 10 characters'); return; }
    if (refundType === 'PARTIAL') {
      const amt = parseFloat(refundAmount);
      if (isNaN(amt) || amt <= 0) { setRefundError('Please enter a valid amount'); return; }
      // BR-25: the ceiling is the service the garage supplied, not the payment total — the delivery
      // fee is refunded through the checkbox, never by typing it into this field.
      if (serviceCeiling != null && amt > serviceCeiling + 0.0001) {
        setRefundError('Refund exceeds the amount paid');
        return;
      }
    }
    setSubmitting(true);
    try {
      const request: ProcessRefundRequest = {
        refundType,
        amount: refundType === 'PARTIAL' ? parseFloat(refundAmount) : undefined,
        reason: refundReason,
        faultParty,
        feeBorneBy,
        refundDeliveryFee: refundDeliveryFee ? true : undefined,
        // Spec 068 — only an explicit tick/untick; omitted, billing applies its fault-party default.
        refundServiceFee,
      };
      await ordersApi.processRefund(orderId, order, request, auth.token);
      setModal(null);
      resetRefundForm();
      fetchOrder();
    } catch (err) {
      // 400 (BR-4 fault party missing) and 422 (BR-8b ceiling) carry their message in the body;
      // ApiError already surfaces it, so only the generic fallback needs a status hint.
      if (err instanceof ApiError) {
        setRefundError(err.message.startsWith('HTTP error!') ? `Refund rejected (${err.status})` : err.message);
      } else {
        setRefundError(err instanceof Error ? err.message : 'Refund failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-loading-spinner"></div>
        <p style={{ marginTop: '0.75rem', color: 'var(--admin-text-muted)' }}>Loading order...</p>
      </div>
    );
  }

  if (error) return <div className="admin-info-banner danger">{error}</div>;
  if (!order) return <div className="admin-info-banner danger">Order not found</div>;

  const pickup = order.pickupDeliveries?.find((d) => d.type === 'PICKUP');
  const dropoff = order.pickupDeliveries?.find((d) => d.type === 'RETURN_DELIVERY');
  const canDispute = DISPUTABLE_STATUSES.includes(order.status);
  const promo = orderPromotion(order);
  const serviceFee = orderServiceFee(order);
  const options = selectedOptions(order);
  const amountPaid = orderAmountPaid(order);
  const canReassign = REASSIGNABLE_STATUSES.includes(order.status);
  // #641 — a SELF leg can never take a driver, so it gets its method instead of an assign button.
  const pickupTakesDriver = legTakesDriver(order.pickupMethod);
  const returnTakesDriver = legTakesDriver(order.returnMethod);
  // Spec 072 v2 — Assign driver lists only the order's transport type. A null type on an order the
  // admin can assign is a legacy one, which orders treats as RECOVERY, so the list follows that.
  const assignTransport = order.transportType ?? 'RECOVERY';

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' }}>
        <button onClick={() => navigate('/orders')} className="admin-btn admin-btn-outline admin-btn-sm">
          Back
        </button>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--admin-text)', margin: 0 }}>
          {order.orderNumber ?? 'Order Detail'}
        </h2>
        <OrderStatusBadge status={order.status} />
        <TransportBadge type={order.transportType} testId="order-transport-type" />
        {/* #1181 — the day and slot the customer reserved, first thing an admin needs on a call. */}
        {formatScheduled(order.scheduledDate, order.pickupTimeSlotLabel) && (
          <span data-testid="order-scheduled" style={{ color: 'var(--admin-text-muted)', fontSize: '0.9rem' }}>
            Scheduled: {formatScheduled(order.scheduledDate, order.pickupTimeSlotLabel)}
          </span>
        )}
        {/* Spec 077 — the return slot; a null return date means the booking day. */}
        {order.returnMethod === 'GARAGAT' && formatScheduled(order.returnDate ?? order.scheduledDate, order.returnTimeSlotLabel) && (
          <span data-testid="order-return-scheduled" style={{ color: 'var(--admin-text-muted)', fontSize: '0.9rem' }}>
            Return: {formatScheduled(order.returnDate ?? order.scheduledDate, order.returnTimeSlotLabel)}
          </span>
        )}
      </div>

      {/* Spec 052 AC-42: gateway chargeback — distinct from the DISPUTED order status */}
      <ChargebackBanner disputes={disputes} />

      {/* Resolution banner */}
      {order.resolutionMessage && (
        <div className="order-resolution-banner">
          <div className="order-resolution-title">
            Dispute resolved
            {order.resolutionRefundPercent != null && ` — ${order.resolutionRefundPercent}% refund`}
          </div>
          <div className="order-resolution-message">{order.resolutionMessage}</div>
        </div>
      )}

      {/* State-aware action bar */}
      <div className="order-action-bar">
        {/* Spec 056 — a SELF leg still shows WHY there is no driver to assign (#641), but the badge
            is no longer the whole story: the customer brings the car themselves, so somebody has to
            record that it arrived. The API has always allowed the admin here (mark-in-progress
            delegates to OrderService.vehicleReceived, which accepts CREATED on a self pickup); only
            the button was missing, which left an admin looking at a self-delivery order with no way
            to move it at all. */}
        {order.status === 'CREATED' && (pickupTakesDriver ? (
          <button className="admin-btn admin-btn-primary admin-btn-sm" onClick={() => setModal('assignPickup')}>
            Assign pickup driver
          </button>
        ) : (
          <>
            <span className="admin-badge" data-testid="pickup-method-note">
              Pickup: {legMethodLabel('pickup', order.pickupMethod, order.transportType)}
            </span>
            <button
              className="admin-btn admin-btn-primary admin-btn-sm"
              data-testid="mark-vehicle-received"
              onClick={handleMarkInProgress}
            >
              Vehicle received
            </button>
          </>
        ))}
        {order.status === 'PICKUP_ASSIGNED' && (
          <button className="admin-btn admin-btn-primary admin-btn-sm" onClick={handleMarkInProgress}>
            Mark in progress
          </button>
        )}
        {order.status === 'SERVICE_COMPLETE' && (returnTakesDriver ? (
          <button className="admin-btn admin-btn-primary admin-btn-sm" onClick={() => setModal('assignDropoff')}>
            Assign drop-off driver
          </button>
        ) : (
          <>
            <span className="admin-badge" data-testid="return-method-note">
              Drop-off: {legMethodLabel('return', order.returnMethod, order.transportType)}
            </span>
            <button
              className="admin-btn admin-btn-primary admin-btn-sm"
              data-testid="mark-complete-self-return"
              onClick={handleMarkComplete}
            >
              Mark complete
            </button>
          </>
        ))}
        {order.status === 'DROPOFF_ASSIGNED' && (
          <button className="admin-btn admin-btn-primary admin-btn-sm" onClick={handleMarkComplete}>
            Mark complete
          </button>
        )}
        {canReassign && (
          <button className="admin-btn admin-btn-primary admin-btn-sm" onClick={() => setModal('reassign')}>
            Reassign driver
          </button>
        )}
        {order.rescheduleAllowed && (
          <button
            className="admin-btn admin-btn-primary admin-btn-sm"
            data-testid="reschedule-open"
            onClick={() => setModal('reschedule')}
          >
            Reschedule
          </button>
        )}
        {canDispute && (
          <button className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => setModal('disputes')}>
            Send to disputes
          </button>
        )}
        {order.status === 'DISPUTED' && (
          <button className="admin-btn admin-btn-primary admin-btn-sm" onClick={() => setModal('resolve')}>
            Resolve dispute
          </button>
        )}
      </div>

      {/* Parties */}
      <div className="order-parties-grid">
        <div className="admin-card">
          <h3 className="admin-card-title" style={{ marginBottom: '1rem' }}>Customer</h3>
          <div className="admin-detail-grid">
            <Field
              label="Name"
              value={
                <Link to={`/users/${order.customerId}`} style={{ color: 'var(--admin-primary)' }}>
                  {order.customerName ?? order.customerId}
                </Link>
              }
            />
            <Field label="Phone" value={order.customerPhone} />
          </div>
        </div>

        <div className="admin-card">
          <h3 className="admin-card-title" style={{ marginBottom: '1rem' }}>Garage</h3>
          <div className="admin-detail-grid">
            <Field label="Name" value={order.garageName} />
            <Field
              label="Garage ID"
              value={<span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{order.garageId}</span>}
            />
          </div>
        </div>

        <div className="admin-card">
          <h3 className="admin-card-title" style={{ marginBottom: '1rem' }}>Vehicle</h3>
          <div className="admin-detail-grid">
            <Field label="Make / Model" value={order.vehicleMakeModel} />
            <Field label="Plate" value={order.vehiclePlate} />
          </div>
        </div>

        <div className="admin-card">
          <h3 className="admin-card-title" style={{ marginBottom: '1rem' }}>Service &amp; Payment</h3>
          <div className="admin-detail-grid">
            <Field label="Service" value={order.serviceName} />
            {/* #1193 — any selection, a single variant's picked values included; a flat service has none. */}
            {options.length > 0 && (
              <Field
                label="Options"
                value={
                  <ul data-testid="service-lines" style={{ margin: 0, paddingLeft: 18 }}>
                    {options.map((l, i) => (
                      <li key={i}>
                        {l.label} — {Number(l.price).toFixed(2)} {order.currency}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}
            {/* The garage's supply. Garagat's delivery fee is the separate line below — the
                customer's card was charged their sum, which no field here states. */}
            <Field label="Service amount" value={`${order.totalAmount.toFixed(2)} ${order.currency}`} />
            {(order.deliveryFeeGross ?? 0) > 0 && (
              <Field
                label="Pickup & delivery (Garagat)"
                value={`${(order.deliveryFeeGross as number).toFixed(2)} ${order.currency}${
                  order.deliveryTrips ? ` · ${order.deliveryTrips} trip${order.deliveryTrips === 1 ? '' : 's'}` : ''
                }`}
              />
            )}
            {/* Spec 068 V9 — Garagat's own service fee, only when the order carries one (> 0). */}
            {serviceFee && (
              <Field
                label="Service fee (Garagat)"
                value={
                  <span data-testid="order-service-fee-line">
                    <span style={{ display: 'inline-flex', alignItems: 'center', fontVariantNumeric: 'tabular-nums' }}>
                      {serviceFee.gross.toFixed(2)} {order.currency}
                      <InfoTip glossaryKey="serviceFee" />
                    </span>
                    {serviceFee.vat != null && (
                      <span className="service-fee-vat-note" data-testid="order-service-fee-vat">
                        incl. VAT {serviceFee.vat.toFixed(2)} {order.currency}
                      </span>
                    )}
                  </span>
                }
              />
            )}
            {/* Spec 059 AC-66 — Garagat funds the discount; the service amount above stays at full price. */}
            {promo && (
              <>
                <Field
                  label={`Garagat promotion (${promo.code})`}
                  value={
                    <span data-testid="order-promo-line">
                      −{promo.discount.toFixed(2)} {order.currency}
                      {promo.codeId && (
                        <>
                          {' · '}
                          <Link to={`/promotions/${promo.codeId}`} style={{ color: 'var(--admin-primary)' }}>{promo.code}</Link>
                        </>
                      )}
                    </span>
                  }
                />
              </>
            )}
            {/* Spec 068 AC-24 — what the customer was charged, on every order, promo or not. */}
            {amountPaid != null && (
              <Field label="Amount paid" value={<span data-testid="order-amount-paid">{amountPaid.toFixed(2)} {order.currency}</span>} />
            )}
            <Field
              label="Refund ID"
              value={order.refundId ? <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{order.refundId}</span> : null}
            />
          </div>
        </div>

        {/* Spec 051 AC-20 + Spec 052: provider-aware payment block, degrades to UUID + retry */}
        <PaymentBlock
          paymentId={order.paymentId}
          payment={payment}
          loading={paymentLoading}
          failed={paymentFailed}
          onRetry={fetchPayment}
        />

        {/* #634 / #641 — per leg: method, driver, recovery provider, status, frozen cost. */}
        <OrderLegsPanel order={order} />

        <div className="admin-card">
          <h3 className="admin-card-title" style={{ marginBottom: '1rem' }}>Order Information</h3>
          <div className="admin-detail-grid">
            <Field label="Order ID" value={<span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{order.id}</span>} />
            <Field label="Scheduled" value={formatScheduled(order.scheduledDate, order.pickupTimeSlotLabel) ?? '—'} />
            <Field label="Created" value={new Date(order.createdAt).toLocaleString()} />
            {order.completedAt && <Field label="Completed" value={new Date(order.completedAt).toLocaleString()} />}
            {order.isSettled && (
              <Field label="Settlement ID" value={<span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{order.settlementId}</span>} />
            )}
          </div>
        </div>
      </div>

      {/* Refund */}
      <div className="admin-card" style={{ marginBottom: '1rem' }}>
        <h3 className="admin-card-title" style={{ marginBottom: '1rem' }}>Refund Status</h3>
        {order.refundId ? (
          <div className="admin-info-banner success">
            {/* #468 — what was actually returned, not just an id. refundedAmount is the gross the
                customer got back (Garagat's delivery-fee slice included, because that is what their
                card shows); the GCN is the customer's credit note, a different document from the
                settlement commission credit note badged below. */}
            <div>
              {order.refundedAmount != null
                ? `Refunded ${order.refundedAmount.toFixed(2)} ${order.currency}`
                : 'This order has been refunded'}
              {order.refundedAt && ` on ${new Date(order.refundedAt).toLocaleString()}`}
              {order.creditNoteNumber && (
                <span
                  className="admin-badge update-requested"
                  style={{ marginLeft: '0.75rem', cursor: order.creditNoteDocumentId ? 'pointer' : 'default' }}
                  title={order.creditNoteDocumentId
                    ? 'Open the customer credit note (PDF)'
                    : 'Customer credit note'}
                  onClick={() => order.creditNoteDocumentId && openCreditNote(order.creditNoteDocumentId)}
                >
                  Credit note {order.creditNoteNumber}
                </span>
              )}
            </div>
            {creditNoteError && (
              <div style={{ fontSize: '0.8rem', marginTop: '0.35rem' }}>{creditNoteError}</div>
            )}
            <div style={{ fontSize: '0.8rem', marginTop: '0.35rem' }}>
              Refund ID: <span style={{ fontFamily: 'monospace' }}>{order.refundId}</span>
              {order.refundTransactionRef && (
                <> · Provider ref: <span style={{ fontFamily: 'monospace' }}>{order.refundTransactionRef}</span></>
              )}
            </div>
            {creditNote && (
              <span
                className="admin-badge update-requested"
                style={{ marginLeft: '0.75rem', cursor: 'pointer' }}
                title={`Commission credit note against ${creditNote.originalInvoiceNumber} — open settlement`}
                onClick={() => navigate(`/settlements/${order.settlementId}`)}
              >
                Credit note {creditNote.number}
              </span>
            )}
          </div>
        ) : order.refundEligible ? (
          <div>
            <div className="admin-info-banner info" style={{ marginBottom: '1rem' }}>
              Refund eligible until: {order.refundWindowExpiresAt ? new Date(order.refundWindowExpiresAt).toLocaleString() : 'N/A'}
            </div>
            <button className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => setModal('refund')}>
              Process Refund
            </button>
          </div>
        ) : (
          <div className="admin-info-banner warning">
            Refund window has expired or order is not eligible for refund.
          </div>
        )}
      </div>

      {/* Invoice (Spec 051 Flow C) */}
      <InvoicePanel orderId={order.id} orderPaid={!!order.paymentId} token={auth.token} />

      {/* Timeline */}
      <div className="admin-card">
        <h3 className="admin-card-title" style={{ marginBottom: '1rem' }}>Timeline</h3>
        {!order.timeline || order.timeline.length === 0 ? (
          <div className="admin-empty-state"><p>No timeline events</p></div>
        ) : (
          <div style={{ position: 'relative', paddingLeft: '1.5rem' }}>
            {order.timeline.map((event: OrderTimelineEvent) => (
              <div key={event.id} style={{
                position: 'relative', paddingBottom: '1.25rem', paddingLeft: '1rem',
                borderLeft: '2px solid var(--admin-border)',
              }}>
                <div style={{
                  position: 'absolute', left: '-0.375rem', top: '0.25rem',
                  width: '0.625rem', height: '0.625rem', borderRadius: '50%',
                  background: 'var(--admin-primary)',
                }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                    {event.eventType.replace(/_/g, ' ')}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>
                    {new Date(event.createdAt).toLocaleString()}
                  </span>
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem' }} data-testid="timeline-actor">
                  {/* Spec 069 AC-D1 — the server label, or a name-less fallback; never a bare subject id. */}
                  <ActorChip
                    label={event.actorLabel}
                    role={event.actorRole}
                    name={event.actorName}
                    suffix={event.garageName}
                    subjectId={event.actorId}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ToastView toast={toast} />

      {/* ── Modals ── */}
      {modal === 'assignPickup' && pickupTakesDriver && (
        <AssignDriverModal kind="pickup" transportType={assignTransport} onSubmit={handleAssignPickup} onClose={() => setModal(null)} />
      )}
      {modal === 'assignDropoff' && returnTakesDriver && (
        <AssignDriverModal kind="dropoff" transportType={assignTransport} onSubmit={handleAssignDropoff} onClose={() => setModal(null)} />
      )}
      {modal === 'reassign' && (
        <AssignDriverModal
          kind={order.status.startsWith('PICKUP_') ? 'pickup' : 'dropoff'}
          transportType={assignTransport}
          currentProvider={legProvider(order.status.startsWith('PICKUP_') ? pickup : dropoff)}
          onSubmit={handleReassign}
          onClose={() => setModal(null)}
        />
      )}
      {modal === 'reschedule' && (
        <RescheduleOrderModal
          order={order}
          onSuccess={handleRescheduled}
          onConflict={handleRescheduleConflict}
          onClose={() => setModal(null)}
        />
      )}
      {modal === 'disputes' && (
        <AdminCancelOrderModal orderId={order.id} onSuccess={handleDisputesSuccess} onClose={() => setModal(null)} />
      )}
      {modal === 'resolve' && (
        <ResolveDisputeModal
          onSubmit={handleResolve}
          onClose={() => setModal(null)}
          paymentId={order.paymentId}
          totalAmount={order.totalAmount}
          deliveryFeeGross={order.deliveryFeeGross}
          deliveryTrips={order.deliveryTrips}
          currency={order.currency}
          garageName={order.garageName}
          token={auth.token ?? undefined}
        />
      )}

      {modal === 'refund' && (
        <div className="admin-modal-overlay" onClick={closeRefundModal}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Process Refund</h3>
              <button className="admin-modal-close" onClick={closeRefundModal}>&times;</button>
            </div>
            <div className="admin-modal-body">
              {order.refundWindowExpiresAt && (
                <div className="admin-info-banner info" style={{ marginBottom: '1rem' }}>
                  Refund window expires: {new Date(order.refundWindowExpiresAt).toLocaleString()}
                </div>
              )}

              <div className="admin-form-group">
                <label className="admin-form-label">Refund Type</label>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input type="radio" checked={refundType === 'FULL'} onChange={() => setRefundType('FULL')} />
                    Full Refund (<span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {(serviceGross ?? order.totalAmount).toFixed(2)} {order.currency}
                    </span>{(order.deliveryFeeGross ?? 0) > 0 ? ' service' : ''})
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input type="radio" checked={refundType === 'PARTIAL'} onChange={() => setRefundType('PARTIAL')} />
                    Partial Refund
                  </label>
                </div>
              </div>

              {refundType === 'PARTIAL' && (
                <div className="admin-form-group">
                  <label className="admin-form-label" htmlFor="refund-amount">
                    Service amount ({order.currency})
                  </label>
                  <input
                    id="refund-amount"
                    type="number"
                    className="admin-form-input"
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    max={serviceCeiling ?? order.totalAmount}
                    min={0.01}
                    step={0.01}
                    placeholder="Enter refund amount"
                    aria-describedby="refund-amount-help"
                  />
                  <p id="refund-amount-help" style={{ fontSize: '0.8125rem', color: 'var(--admin-text-muted)', margin: '0.25rem 0 0' }}>
                    {serviceCeiling != null
                      ? `Up to ${order.currency} ${serviceCeiling.toFixed(2)} — the service the garage supplied${
                          (deliveryFeeCharged ?? 0) > 0 ? ', excluding the pickup & delivery fee' : ''
                        }.`
                      : 'The service the garage supplied.'}
                  </p>
                </div>
              )}

              <DeliveryFeeRefundCheckbox
                feeCharged={deliveryFeeCharged}
                trips={order.deliveryTrips}
                currency={order.currency}
                checked={refundDeliveryFee}
                onChange={setRefundDeliveryFee}
                disabled={submitting}
              />

              <div className="admin-form-group" role="radiogroup" aria-labelledby="refund-fault-party-label">
                <label className="admin-form-label" id="refund-fault-party-label">Who is responsible?</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                  {FAULT_PARTIES.map((party) => (
                    <label key={party} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="refund-fault-party"
                        value={party}
                        checked={faultParty === party}
                        onChange={() => selectFaultParty(party)}
                      />
                      {FAULT_PARTY_LABELS[party]}
                    </label>
                  ))}
                </div>
              </div>

              {/* Spec 068 V11 — appears once the preview (which needs the fault party) states a fee > 0. */}
              <ServiceFeeRefundCheckbox
                feeCharged={serviceFeeCharged}
                currency={order.currency}
                checked={serviceFeeTicked}
                onChange={setRefundServiceFee}
                disabled={submitting}
                borneBy={refundPreview?.serviceFeeBorneBy ?? null}
              />

              <div className="admin-form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={platformFeeTicked}
                    onChange={(e) => setPlatformBearsFee(e.target.checked)}
                  />
                  Refund the payment processing fee too (Garagat absorbs it)
                </label>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Reason (min 10 characters)</label>
                <textarea
                  className="admin-textarea"
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  rows={3}
                  placeholder="Enter reason for refund..."
                />
              </div>

              <div className="admin-form-group">
                <RefundConsequence
                  preview={refundPreview}
                  loading={refundPreviewLoading}
                  error={refundPreviewError}
                  faultParty={faultParty}
                  enabled={previewEnabled}
                  currency={order.currency}
                  garageName={order.garageName}
                />
              </div>

              {refundError && <div className="admin-info-banner danger">{refundError}</div>}
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn-outline" onClick={closeRefundModal} disabled={submitting}>
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-danger"
                onClick={handleRefundSubmit}
                disabled={submitting || !faultParty || !!refundPreviewPending}
                title={!faultParty ? 'Select who is responsible for this refund' : refundPreviewPending ? 'Calculating the refund preview…' : undefined}
              >
                {submitting ? 'Processing...' : 'Confirm Refund'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
